import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import AttendanceFormModal from '../../components/Modals/AttendanceFormModal';
import AttendanceSlideModal from '../../components/Modals/AttendanceSlideModal';
import { MonthType, useAttendance, AttendanceResponseDto } from '../../hooks/useAttendance';
import { useStudents, Student } from '../../hooks/useStudents';
import { useSessionClass } from 'contexts/SessionClassContext';
import EnhancedTable from '../common/EnhancedTable';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'contexts/AuthContext';
import { useTenantHeader } from 'hooks/useTenantHeader';
import ActionButtons from '../common/ActionButtons';
import { useTeacher } from 'hooks/useTeacher';
import { format, parseISO, isValid } from 'date-fns';

const AttendanceManagement: React.FC = () => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editDate, setEditDate] = useState<string | null>(null);
  const [editTeacherId, setEditTeacherId] = useState<number | null>(null);
  const [oneByOneSetupOpen, setOneByOneSetupOpen] = useState(false);
  const [slideModalOpen, setSlideModalOpen] = useState(false);
  const [oneByOneTeacherId, setOneByOneTeacherId] = useState<number | ''>('');
  const [oneByOneMonth, setOneByOneMonth] = useState<MonthType>('MAY');
  const [oneByOneYear, setOneByOneYear] = useState<number>(new Date().getFullYear());
  const [oneByOneDay, setOneByOneDay] = useState<number>(1);
  const { selectedSession, selectedClass } = useSessionClass();

  const now = new Date();
  const [month, setMonth] = useState<MonthType>((() => {
    const m = now.getMonth();
    const months: MonthType[] = [
      'JANUARY',
      'FEBRUARY',
      'MARCH',
      'APRIL',
      'MAY',
      'JUNE',
      'JULY',
      'AUGUST',
      'SEPTEMBER',
      'OCTOBER',
      'NOVEMBER',
      'DECEMBER',
    ];
    return months[m] || 'JANUARY';
  })());
  const [year, setYear] = useState<number>(now.getFullYear());

  const { getAttendanceQuery } = useAttendance({
    sessionId: selectedSession?.id || 0,
    classId: selectedClass?.id || 0,
    month,
    year,
  });

  const { user } = useAuth();
  const { tenantLoading } = useTenantHeader();
  const { studentsQuery } = useStudents(tenantLoading);
  const { teachersQuery } = useTeacher(user?.schoolId || 0, tenantLoading);

  const handleTakeAttendance = () => {
    setEditDate(null);
    setEditTeacherId(null);
    setModalOpen(true);
  };

  const handleOpenOneByOneSetup = () => {
    setOneByOneSetupOpen(true);
    // default teacher: current user mapped teacher
    const currentTeacher = teachersQuery.data?.find(tt => tt.userId === user?.id);
    setOneByOneTeacherId(currentTeacher?.id ?? '');
    setOneByOneMonth(month === 'NA' ? 'MAY' : month);
    setOneByOneYear(year);
    setOneByOneDay(1);
  };

  const handleCloseModal = () => {
    setEditDate(null);
    setEditTeacherId(null);
    setModalOpen(false);
  };

  const handleCloseOneByOneSetup = () => {
    setOneByOneSetupOpen(false);
  };

  const handleNextOneByOne = () => {
    if (!oneByOneTeacherId || !oneByOneDay) return;
    setOneByOneSetupOpen(false);
    setSlideModalOpen(true);
  };

  const handleCloseSlideModal = () => {
    setSlideModalOpen(false);
  };

  const handleEditAttendance = (date: string, staffId?: number | null) => {
    setEditDate(date);
    setEditTeacherId(staffId ?? null);
    setModalOpen(true);
  };

  // Delete functionality is removed as it's not supported by the new API
  const handleCloseDeleteDialog = () => { }; // Placeholder
  const handleDelete = async () => { }; // Placeholder

  const getStudentName = (studentId: number) => {
    const student = studentsQuery.data?.find((s: Student) => s.id === studentId);
    return student ? `${student.studentName}` : '';
  };

  const columns = [
    { id: 'displayDate', label: t('date'), minWidth: 130 },
    { id: 'className', label: t('class'), minWidth: 130 },
    { id: 'teacherName', label: t('teacher'), minWidth: 150 },
    { id: 'statusSummary', label: t('status'), minWidth: 200 },
    { id: 'lastUpdated', label: t('last_updated'), minWidth: 150 },
  ];

  const renderActions = (row: any) => (
    <ActionButtons
      onEdit={() => handleEditAttendance(row.date, row.staffId)}
    />
  );

  const filteredAttendance = getAttendanceQuery.data || [];

  const safeFormatIso = (value: string, fmt: string) => {
    try {
      const d = parseISO(value);
      if (!isValid(d)) return value || t('n/a');
      return format(d, fmt);
    } catch {
      return value || t('n/a');
    }
  };

  const groupedData = useMemo(() => {
    const groups: Record<string, {
      date: string,
      presents: number,
      absents: number,
      leaves: number,
      total: number,
      classId: number,
      staffId: number | null,
      lastUpdatedTime: string | null
    }> = {};

    filteredAttendance.forEach((record) => {
      const date = record.attendanceDate;
      if (!groups[date]) {
        groups[date] = {
          date,
          presents: 0,
          absents: 0,
          leaves: 0,
          total: 0,
          classId: record.classId,
          staffId: record.staffId || record.teacherId || null,
          lastUpdatedTime: record.lastUpdatedTime || null
        };
      }
      groups[date].total++;
      if (record.statusCode === 'PRESENT') {
        groups[date].presents++;
      } else if (record.statusCode === 'ABSENT') {
        groups[date].absents++;
      } else if (record.statusCode === 'LEAVE') {
        groups[date].leaves++;
      }

      if (record.lastUpdatedTime) {
        const currentGroup = groups[date];
        if (!currentGroup.lastUpdatedTime || record.lastUpdatedTime > currentGroup.lastUpdatedTime) {
          currentGroup.lastUpdatedTime = record.lastUpdatedTime;
        }
      }
    });

    return Object.values(groups).map(group => {
      const teacher = teachersQuery.data?.find(t => t.id === group.staffId);
      const teacherName = teacher ? `${teacher.firstName} ${teacher.lastName}` : t('n/a');

      return {
        ...group,
        displayDate: safeFormatIso(group.date, month === 'NA' ? "MMM yyyy" : "dd-MM-yyyy"),
        className: selectedClass?.name || String(group.classId),
        teacherName,
        statusSummary: `${t('total')}: ${group.total}, ${t('present')}: ${group.presents}, ${t('absent')}: ${group.absents}, ${t('leave')}: ${group.leaves}`,
        lastUpdated: group.lastUpdatedTime ? safeFormatIso(group.lastUpdatedTime, "dd-MM-yyyy HH:mm") : t('n/a'),
      };
    }).sort((a, b) => b.date.localeCompare(a.date));
  }, [filteredAttendance, selectedClass, t, teachersQuery.data]);



  return (
    <Paper sx={{ p: 2, maxWidth: 1500, mx: 'auto', mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">{t('attendance_management')}</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: "#3092e3",
              "&:hover": { backgroundColor: "primary.main" }
            }}
            onClick={handleTakeAttendance}
          >
            {t('take_attendance')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: "#1976d2",
              "&:hover": { backgroundColor: "#1565c0" }
            }}
            onClick={handleOpenOneByOneSetup}
          >
            {t('one_by_one_attendance')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>{t('month')}</InputLabel>
          <Select
            label={t('month')}
            value={month}
            onChange={(e) => setMonth(e.target.value as MonthType)}
          >
            <MenuItem value="NA">{t('all')}</MenuItem>
            <MenuItem value="JANUARY">{t('january')}</MenuItem>
            <MenuItem value="FEBRUARY">{t('february')}</MenuItem>
            <MenuItem value="MARCH">{t('march')}</MenuItem>
            <MenuItem value="APRIL">{t('april')}</MenuItem>
            <MenuItem value="MAY">{t('may')}</MenuItem>
            <MenuItem value="JUNE">{t('june')}</MenuItem>
            <MenuItem value="JULY">{t('july')}</MenuItem>
            <MenuItem value="AUGUST">{t('august')}</MenuItem>
            <MenuItem value="SEPTEMBER">{t('september')}</MenuItem>
            <MenuItem value="OCTOBER">{t('october')}</MenuItem>
            <MenuItem value="NOVEMBER">{t('november')}</MenuItem>
            <MenuItem value="DECEMBER">{t('december')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('year')}</InputLabel>
          <Select
            label={t('year')}
            value={String(year)}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {Array.from({ length: 7 }, (_, i) => now.getFullYear() - 3 + i).map((y) => (
              <MenuItem key={y} value={String(y)}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <EnhancedTable
        columns={columns}
        data={groupedData}
        title={t('attendance_list')}
        searchFields={['className', 'date']}
        filterFields={[]}
        actions={renderActions}
      />

      <AttendanceFormModal
        open={modalOpen}
        onClose={handleCloseModal}
        initialDate={editDate}
        initialTeacherId={editTeacherId}
      />

      <Dialog open={oneByOneSetupOpen} onClose={handleCloseOneByOneSetup} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">{t('one_by_one_attendance')}</Typography>
          <IconButton onClick={handleCloseOneByOneSetup} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
              <InputLabel>{t('teacher_name')}</InputLabel>
              <Select
                label={t('teacher_name')}
                value={oneByOneTeacherId === '' ? '' : String(oneByOneTeacherId)}
                onChange={(e) => setOneByOneTeacherId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <MenuItem value="">{t('select')}</MenuItem>
                {teachersQuery.data?.map((teacher) => (
                  <MenuItem key={teacher.id} value={String(teacher.id)}>
                    {teacher.firstName} {teacher.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{t('month')}</InputLabel>
              <Select
                label={t('month')}
                value={oneByOneMonth}
                onChange={(e) => setOneByOneMonth(e.target.value as MonthType)}
              >
                <MenuItem value="JANUARY">{t('january')}</MenuItem>
                <MenuItem value="FEBRUARY">{t('february')}</MenuItem>
                <MenuItem value="MARCH">{t('march')}</MenuItem>
                <MenuItem value="APRIL">{t('april')}</MenuItem>
                <MenuItem value="MAY">{t('may')}</MenuItem>
                <MenuItem value="JUNE">{t('june')}</MenuItem>
                <MenuItem value="JULY">{t('july')}</MenuItem>
                <MenuItem value="AUGUST">{t('august')}</MenuItem>
                <MenuItem value="SEPTEMBER">{t('september')}</MenuItem>
                <MenuItem value="OCTOBER">{t('october')}</MenuItem>
                <MenuItem value="NOVEMBER">{t('november')}</MenuItem>
                <MenuItem value="DECEMBER">{t('december')}</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>{t('year')}</InputLabel>
              <Select
                label={t('year')}
                value={String(oneByOneYear)}
                onChange={(e) => setOneByOneYear(Number(e.target.value))}
              >
                {Array.from({ length: 7 }, (_, i) => now.getFullYear() - 3 + i).map((y) => (
                  <MenuItem key={y} value={String(y)}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>{t('day')}</InputLabel>
              <Select
                label={t('day')}
                value={String(oneByOneDay)}
                onChange={(e) => setOneByOneDay(Number(e.target.value))}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <MenuItem key={d} value={String(d)}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseOneByOneSetup} variant="outlined" sx={{ borderColor: "#3092e3", color: "#3092e3" }}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
            onClick={handleNextOneByOne}
            disabled={!oneByOneTeacherId || !oneByOneDay}
          >
            {t('next')}
          </Button>
        </DialogActions>
      </Dialog>

      <AttendanceSlideModal
        {...({
          open: slideModalOpen,
          onClose: handleCloseSlideModal,
          presetTeacherId: oneByOneTeacherId === '' ? undefined : Number(oneByOneTeacherId),
          presetMonth: oneByOneMonth,
          presetYear: oneByOneYear,
          presetDay: oneByOneDay || undefined,
        } as any)}
      />

    </Paper>
  );
};

export default AttendanceManagement;